import { Col, Row } from 'antd';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { ListPage } from 'src/components/ListPage/ListPage';
import { ListPageItem } from 'src/components/ListPage/ListPageItem';
import { RoutePaths } from 'src/core/router/RoutePaths';
import { EntityStatus } from 'src/core/StatusInfo';
import { LoanDtoBorrowerAllowed } from 'src/generated-api-client';
import { LoanStatusInfoMap } from 'src/pages/LoansPage/LoanStatusInfo';
import { LoansStore } from 'src/stores/LoansStore/LoansStore';
import { SettingsStore } from 'src/stores/SettingsStore/SettingsStore';
import { DateHelper } from 'src/utils/DateHelper';
import { NumberHelper } from 'src/utils/NumberHelper';
import { UrlHelper } from 'src/utils/UrlHelper';
import styled from 'styled-components';

export const LoansPage = observer(() => {
    const { t } = useTranslation();

    return (
        <ListPage
            pageName="loans"
            store={LoansStore}
            title={t('Page.Loans.Title')}
            renderItem={(item: LoanDtoBorrowerAllowed) => {
                const { term } = item;
                const amountApproved =
                    item.loanApplication?.amountApproved !== undefined
                        ? NumberHelper.formatToCurrency(
                              item.loanApplication.amountApproved,
                              {
                                  currency: SettingsStore.currencyMnemonic,
                              },
                          )
                        : '';

                const targetUrl = _.isNil(item.id)
                    ? item.id
                    : UrlHelper.getTargetUrl(RoutePaths.loan, {
                          loanId: item.id.toString(),
                      });

                return (
                    <ListPageItem
                        to={targetUrl}
                        key={item.id}
                        data-testid={`list-page-item-${item.id}`}
                    >
                        <Row wrap={false} gutter={5}>
                            <Col flex="auto">
                                <StyledName data-testid="loan-details">
                                    <Trans
                                        i18nKey="Page.Loans.ListItem.Title"
                                        count={term}
                                    >
                                        {{ amountApproved }} for {{ term }}
                                    </Trans>

                                    {t(
                                        `loan.period.type.${item?.loanProduct?.campaign?.termPeriodType}`,
                                    )}
                                </StyledName>
                            </Col>
                            <Col flex="none">
                                <EntityStatus
                                    entityName="loan"
                                    statusInfoMap={LoanStatusInfoMap}
                                    status={item.status}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <StyledCreatedAt data-testid="issueDate">
                                    {item.issueDate &&
                                        DateHelper.format(item.issueDate)}
                                </StyledCreatedAt>
                            </Col>
                            <Col>
                                <StyledIdContainer data-testid="loan-number">
                                    #{item?.loanApplication?.number}
                                </StyledIdContainer>
                            </Col>
                        </Row>
                    </ListPageItem>
                );
            }}
        />
    );
});

const StyledName = styled.div`
    font-weight: 600;
    font-size: 30px;
    line-height: 105%;
    letter-spacing: -0.03em;
    color: var(--form-text-color);

    @media (max-width: 576px) {
        font-size: 16px;
    }
`;

const StyledCreatedAt = styled.div`
    font-size: 16px;
    line-height: 165%;
    color: #828b94;
    margin-top: 12px;

    @media (max-width: 576px) {
        font-size: 11px;
    }
`;

const StyledIdContainer = styled.div`
    font-size: 16px;
    line-height: 165%;
    color: #828b94;
    margin-left: 1em;
    margin-top: 12px;

    @media (max-width: 576px) {
        font-size: 11px;
    }
`;
